import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Loader2, Mail, Upload, Star, Image as ImageIcon, KeyRound, Layers, FileSpreadsheet } from "lucide-react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Avatar, BrandMark } from "@/components/BrandMark";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { previewLayoutStyles } from "@/lib/tokens";

/* -------------- Categories -------------- */
function CategoriesPanel() {
    const { categories, reloadCategories, reloadTemplates } = useData();
    const [newName, setNewName] = useState("");
    const [editing, setEditing] = useState(null); // {name, next}
    const [busy, setBusy] = useState(false);

    const add = async () => {
        const n = newName.trim();
        if (!n) return;
        setBusy(true);
        try {
            await api.post("/categories", { name: n });
            toast.success("Category added", { position: "bottom-center" });
            setNewName("");
            await reloadCategories();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const rename = async () => {
        if (!editing) return;
        const next = editing.next.trim();
        if (!next) return;
        setBusy(true);
        try {
            await api.put(`/categories/${encodeURIComponent(editing.name)}`, { new_name: next });
            toast.success("Category renamed", { position: "bottom-center" });
            setEditing(null);
            await reloadCategories();
            await reloadTemplates();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const remove = async (name) => {
        setBusy(true);
        try {
            await api.delete(`/categories/${encodeURIComponent(name)}`);
            toast.success("Category deleted", { position: "bottom-center" });
            await reloadCategories();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div className="tms-card p-6" data-testid="settings-categories">
            <div className="flex items-center justify-between mb-4">
                <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }}>Categories</h2>
                <div className="flex items-center gap-2">
                    <input
                        placeholder="New category name"
                        className="tms-input"
                        style={{ height: 34, padding: "0 12px", fontSize: 13 }}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && add()}
                        data-testid="new-category-input"
                    />
                    <button className="btn-primary-green" onClick={add} disabled={busy} data-testid="new-category-submit-btn">
                        <Plus size={14} /> Add
                    </button>
                </div>
            </div>

            <div className="divide-y" style={{ borderColor: "var(--tms-border)" }}>
                {categories.map((c) => (
                    <div key={c.name} className="flex items-center justify-between py-2" data-testid={`category-row-${c.name}`}>
                        {editing?.name === c.name ? (
                            <div className="flex items-center gap-2 flex-1">
                                <input
                                    className="tms-input"
                                    style={{ height: 32, padding: "0 12px", fontSize: 13 }}
                                    value={editing.next}
                                    onChange={(e) => setEditing({ ...editing, next: e.target.value })}
                                    autoFocus
                                    data-testid={`category-rename-input-${c.name}`}
                                />
                                <button className="btn-primary-navy" onClick={rename} disabled={busy} data-testid={`category-rename-save-${c.name}`}>
                                    <Check size={14} />
                                </button>
                                <button className="btn-outline" onClick={() => setEditing(null)}>
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <>
                                <span style={{ fontSize: 14, color: "var(--tms-navy)", fontWeight: 500 }}>{c.name}</span>
                                <div className="flex items-center gap-2">
                                    <span className="tms-chip-count">{c.usage} in use</span>
                                    <button
                                        className="btn-ghost"
                                        onClick={() => setEditing({ name: c.name, next: c.name })}
                                        data-testid={`category-edit-${c.name}`}
                                    >
                                        <Pencil size={13} />
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={() => remove(c.name)}
                                        disabled={c.usage > 0 || busy}
                                        title={c.usage > 0 ? "In use — can't delete" : "Delete"}
                                        style={{ opacity: c.usage > 0 ? 0.4 : 1 }}
                                        data-testid={`category-delete-${c.name}`}
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* Small controlled height input that commits on blur/Enter to avoid firing
 * a PUT on every keystroke and racing the reload. */
function LogoHeightInput({ initial, onCommit, dataTestId, disabled, min = 16, max = 48, width = 54, height = 22, fontSize = 11 }) {
    const [val, setVal] = useState(String(initial));

    // Keep local state in sync when parent-provided initial changes (e.g. after
    // a reload from the server).
    useEffect(() => {
        setVal(String(initial));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial]);

    const commit = () => {
        const parsed = parseInt(val || String(initial), 10);
        const clamped = Math.max(min, Math.min(max, isNaN(parsed) ? initial : parsed));
        setVal(String(clamped));
        if (clamped !== initial) onCommit(clamped);
    };

    return (
        <input
            type="number"
            min={min}
            max={max}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === "Escape") { setVal(String(initial)); e.currentTarget.blur(); }
            }}
            disabled={disabled}
            className="tms-input"
            style={{ width, height, padding: "0 8px", fontSize }}
            data-testid={dataTestId}
        />
    );
}

/* -------------- Brand logos (per-brand nested panel) -------------- */
function BrandLogosPanel({ brand }) {
    const { reloadBrands, reloadTemplates } = useData();
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [pendingHeight, setPendingHeight] = useState(24);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    // Any mutation to a logo (upload, set-active, height change, delete) MUST
    // refresh the DataContext brands so BrandMark instances across the app
    // (library cards, list view, detail header, editor brand picker) reflect
    // the new state without a page reload.
    const propagate = async () => {
        await load();
        await reloadBrands();
        // Templates carry a brand_key only, but many consumers derive their
        // brand object via brandByKey — reloading templates ensures the card
        // grid re-renders too.
        await reloadTemplates();
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/brands/${brand.key}/logos`);
            setLogos(data);
        } catch (e) {
            // ignore
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brand.key]);

    const onFilePick = (e) => {
        setError("");
        const f = e.target.files?.[0];
        if (!f) return;
        upload(f);
        e.target.value = "";
    };

    const upload = async (file) => {
        setBusy(true);
        setError("");
        const form = new FormData();
        form.append("file", file);
        form.append("display_height", String(pendingHeight));
        try {
            const token = localStorage.getItem("esm_token");
            const res = await fetch(`${API_BASE}/brands/${brand.key}/logos`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(formatApiErrorDetail(err.detail) || `Upload failed (${res.status})`);
            }
            toast.success("Logo uploaded", { position: "bottom-center" });
            await propagate();
        } catch (e) {
            setError(e.message);
            toast.error(e.message, { position: "bottom-center" });
        }
        setBusy(false);
    };

    const setActive = async (logo) => {
        setBusy(true);
        try {
            await api.put(`/brands/${brand.key}/logos/${logo.id}`, { active: true });
            toast.success("Active logo set", { position: "bottom-center" });
            await propagate();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const changeHeight = async (logo, h) => {
        setBusy(true);
        try {
            await api.put(`/brands/${brand.key}/logos/${logo.id}`, { display_height: h });
            await propagate();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const removeLogo = async (logo) => {
        setBusy(true);
        try {
            await api.delete(`/brands/${brand.key}/logos/${logo.id}`);
            toast.success("Logo deleted", { position: "bottom-center" });
            await propagate();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div
            className="mt-3 p-3 rounded-[10px]"
            style={{ background: "#F7F8FA", border: "1px solid var(--tms-border)" }}
            data-testid={`brand-logos-panel-${brand.key}`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <ImageIcon size={13} style={{ color: "var(--tms-navy)" }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--tms-navy)" }}>
                        Logo variants ({logos.length})
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <label style={{ fontSize: 11, color: "var(--tms-muted)" }}>
                        Height on next upload
                    </label>
                    <LogoHeightInput
                        initial={pendingHeight}
                        onCommit={(v) => setPendingHeight(v)}
                        dataTestId={`brand-upload-height-${brand.key}`}
                        width={64}
                        height={28}
                        fontSize={12}
                    />
                    <button
                        type="button"
                        className="btn-outline"
                        onClick={() => fileRef.current?.click()}
                        disabled={busy}
                        data-testid={`brand-upload-btn-${brand.key}`}
                        style={{ height: 30, fontSize: 12 }}
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload logo
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={onFilePick}
                        style={{ display: "none" }}
                        data-testid={`brand-upload-input-${brand.key}`}
                    />
                </div>
            </div>

            {error && (
                <div
                    style={{ background: "#fdeceb", color: "var(--tms-danger)", padding: "6px 10px", borderRadius: 8, fontSize: 12, marginBottom: 10 }}
                    data-testid={`brand-upload-error-${brand.key}`}
                >
                    {error}
                </div>
            )}

            {logos.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--tms-muted)", padding: "8px 0" }}>
                    No logos uploaded yet — the short-code mark is shown as a fallback. PNG, JPG, SVG or WebP up to 2MB.
                </div>
            ) : (
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
                    {logos.map((logo) => (
                        <div
                            key={logo.id}
                            className="tms-card p-3 flex items-center gap-3"
                            style={{ borderColor: logo.active ? "var(--tms-green)" : "var(--tms-border)" }}
                            data-testid={`brand-logo-variant-${logo.id}`}
                        >
                            <div
                                className="flex items-center justify-center"
                                style={{
                                    width: 90,
                                    height: 44,
                                    background: "#fff",
                                    border: "1px solid var(--tms-border)",
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${logo.url}`}
                                    alt={logo.filename}
                                    style={{
                                        maxHeight: 36,
                                        maxWidth: 82,
                                        objectFit: "contain",
                                    }}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-navy)" }} className="truncate">
                                    {logo.filename}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                    <label style={{ fontSize: 10, color: "var(--tms-muted)" }}>Height</label>
                                    <LogoHeightInput
                                        initial={logo.display_height}
                                        onCommit={(v) => changeHeight(logo, v)}
                                        dataTestId={`logo-height-input-${logo.id}`}
                                        disabled={busy}
                                    />
                                    <span style={{ fontSize: 10, color: "var(--tms-faint)" }}>px</span>
                                    {logo.active ? (
                                        <span
                                            className="tms-chip"
                                            style={{ background: "var(--tms-green-tint)", color: "#4d7b1b", border: "1px solid #d3e8a6", height: 20, padding: "0 8px", fontSize: 10 }}
                                            data-testid={`logo-active-badge-${logo.id}`}
                                        >
                                            <Star size={10} /> Active
                                        </span>
                                    ) : (
                                        <button
                                            className="btn-ghost"
                                            style={{ padding: "2px 6px", fontSize: 11 }}
                                            onClick={() => setActive(logo)}
                                            disabled={busy}
                                            data-testid={`logo-set-active-${logo.id}`}
                                        >
                                            Set active
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button
                                className="btn-ghost"
                                onClick={() => removeLogo(logo)}
                                disabled={busy}
                                style={{ padding: "4px 6px" }}
                                data-testid={`logo-delete-${logo.id}`}
                                title="Delete this logo"
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------------- Brands -------------- */
function BrandsPanel() {
    const { brands, reloadBrands, reloadTemplates } = useData();
    const [busy, setBusy] = useState(false);
    const [newBrand, setNewBrand] = useState({ key: "", label: "", short_code: "" });
    const [editing, setEditing] = useState(null); // {key, label, short_code}

    const add = async () => {
        if (!newBrand.key.trim() || !newBrand.label.trim() || !newBrand.short_code.trim()) return;
        setBusy(true);
        try {
            await api.post("/brands", newBrand);
            toast.success("Brand added", { position: "bottom-center" });
            setNewBrand({ key: "", label: "", short_code: "" });
            await reloadBrands();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await api.put(`/brands/${editing.key}`, { label: editing.label, short_code: editing.short_code });
            toast.success("Brand updated", { position: "bottom-center" });
            setEditing(null);
            await reloadBrands();
            await reloadTemplates();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const remove = async (key) => {
        setBusy(true);
        try {
            await api.delete(`/brands/${key}`);
            toast.success("Brand deleted", { position: "bottom-center" });
            await reloadBrands();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div className="tms-card p-6" data-testid="settings-brands">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }}>Brands</h2>
                <div className="flex items-center gap-2">
                    <input placeholder="Key (e.g. tms)" className="tms-input" style={{ height: 34, padding: "0 10px", fontSize: 13, width: 100 }} value={newBrand.key} onChange={(e) => setNewBrand({ ...newBrand, key: e.target.value })} data-testid="new-brand-key-input" />
                    <input placeholder="Label" className="tms-input" style={{ height: 34, padding: "0 10px", fontSize: 13, width: 160 }} value={newBrand.label} onChange={(e) => setNewBrand({ ...newBrand, label: e.target.value })} data-testid="new-brand-label-input" />
                    <input placeholder="Short code" className="tms-input" style={{ height: 34, padding: "0 10px", fontSize: 13, width: 110 }} value={newBrand.short_code} onChange={(e) => setNewBrand({ ...newBrand, short_code: e.target.value })} data-testid="new-brand-shortcode-input" />
                    <button className="btn-primary-green" onClick={add} disabled={busy} data-testid="new-brand-submit-btn"><Plus size={14} /> Add</button>
                </div>
            </div>

            <div className="divide-y" style={{ borderColor: "var(--tms-border)" }}>
                {brands.map((b) => (
                    <div key={b.key} className="py-4" data-testid={`brand-row-${b.key}`}>
                        <div className="flex items-center justify-between gap-3">
                        {editing?.key === b.key ? (
                            <div className="flex items-center gap-2 flex-1">
                                <BrandMark brand={b} code={editing.short_code} size="sm" />
                                <input className="tms-input" style={{ height: 32, padding: "0 10px", fontSize: 13, maxWidth: 220 }} value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                                <input className="tms-input" style={{ height: 32, padding: "0 10px", fontSize: 13, maxWidth: 110 }} value={editing.short_code} onChange={(e) => setEditing({ ...editing, short_code: e.target.value })} />
                                <button className="btn-primary-navy" onClick={save}><Check size={14} /></button>
                                <button className="btn-outline" onClick={() => setEditing(null)}><X size={14} /></button>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-3">
                                    <BrandMark brand={b} code={b.short_code} size="sm" />
                                    <div>
                                        <div style={{ fontSize: 14, color: "var(--tms-navy)", fontWeight: 500 }}>{b.label}</div>
                                        <div style={{ fontSize: 11, color: "var(--tms-muted)" }}>key: {b.key} · short: {b.short_code}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="tms-chip-count">{b.usage} in use</span>
                                    <button className="btn-ghost" onClick={() => setEditing(b)} data-testid={`brand-edit-${b.key}`}><Pencil size={13} /></button>
                                    <button className="btn-ghost" onClick={() => remove(b.key)} disabled={b.usage > 0 || brands.length <= 1 || busy} style={{ opacity: (b.usage > 0 || brands.length <= 1) ? 0.4 : 1 }} data-testid={`brand-delete-${b.key}`}>
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </>
                        )}
                        </div>
                        <BrandLogosPanel brand={b} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/* -------------- Users -------------- */
function UsersPanel() {
    const { user: me } = useAuth();
    const { users, reloadUsers, reloadTemplates } = useData();
    const [busy, setBusy] = useState(false);
    const [invite, setInvite] = useState({ name: "", email: "", role_title: "Mortgage Adviser", access_level: "adviser", temporary_password: "" });
    const [removing, setRemoving] = useState(null);
    const [inviteError, setInviteError] = useState("");
    const [resetTarget, setResetTarget] = useState(null); // user object
    const [resetPassword, setResetPassword] = useState("");
    const [resetError, setResetError] = useState("");

    const sendInvite = async () => {
        setInviteError("");
        if (!invite.name.trim() || !invite.email.trim()) {
            setInviteError("Name and email are required");
            return;
        }
        if (invite.temporary_password.length < 10) {
            setInviteError("Temporary password must be at least 10 characters");
            return;
        }
        setBusy(true);
        try {
            await api.post("/users", invite);
            toast.success(`Invited ${invite.name}`, { position: "bottom-center" });
            setInvite({ name: "", email: "", role_title: "Mortgage Adviser", access_level: "adviser", temporary_password: "" });
            await reloadUsers();
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || "Failed";
            setInviteError(msg);
            toast.error(msg, { position: "bottom-center" });
        }
        setBusy(false);
    };

    const changeRole = async (u, next) => {
        setBusy(true);
        try {
            await api.put(`/users/${u.id}/role`, { access_level: next });
            toast.success(`${u.name} is now ${next}`, { position: "bottom-center" });
            await reloadUsers();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const resend = async (u) => {
        setBusy(true);
        try {
            await api.post(`/users/${u.id}/resend-invite`);
            toast.success(`Invite re-sent to ${u.email}`, { position: "bottom-center" });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const remove = async (u) => {
        setBusy(true);
        try {
            await api.delete(`/users/${u.id}`);
            toast.success(`${u.name} removed. Their templates were transferred to you.`, { position: "bottom-center" });
            setRemoving(null);
            await reloadUsers();
            await reloadTemplates();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const openReset = (u) => {
        setResetTarget(u);
        setResetPassword("");
        setResetError("");
    };

    const closeReset = () => {
        setResetTarget(null);
        setResetPassword("");
        setResetError("");
    };

    const submitReset = async () => {
        setResetError("");
        if (resetPassword.length < 10) {
            setResetError("Temporary password must be at least 10 characters");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPassword });
            toast.success(
                `Password reset for ${resetTarget.name}. Share the temporary password with them privately — they'll be asked to set their own on next sign-in.`,
                { position: "bottom-center", duration: 6000 },
            );
            closeReset();
            await reloadUsers();
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || "Failed";
            setResetError(msg);
            toast.error(msg, { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div className="tms-card p-6" data-testid="settings-users">
            <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }} className="mb-4">Team members</h2>

            <div className="divide-y" style={{ borderColor: "var(--tms-border)" }}>
                {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between py-3 gap-3" data-testid={`user-row-${u.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                            <Avatar initials={u.initials} name={u.name} size="md" />
                            <div className="min-w-0">
                                <div style={{ fontSize: 14, color: "var(--tms-navy)", fontWeight: 500 }}>
                                    {u.name} {u.id === me.id && <span style={{ color: "var(--tms-muted)", fontWeight: 400, fontSize: 12 }}>(you)</span>}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--tms-muted)" }} className="truncate">{u.email}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            {u.status === "invited" ? (
                                <span className="tms-chip status-draft">Invited</span>
                            ) : (
                                <span className="tms-chip status-shared">Active</span>
                            )}
                            {u.status === "invited" && (
                                <button className="btn-ghost" onClick={() => resend(u)} data-testid={`resend-invite-${u.id}`}>
                                    <Mail size={12} /> Resend invite
                                </button>
                            )}
                            {u.id !== me.id && (
                                <button
                                    className="btn-ghost"
                                    onClick={() => openReset(u)}
                                    data-testid={`user-reset-password-${u.id}`}
                                    title="Reset this user's password"
                                >
                                    <KeyRound size={12} /> Reset password
                                </button>
                            )}
                            <select
                                className="tms-select"
                                style={{ height: 32, padding: "0 30px 0 10px", fontSize: 12 }}
                                value={u.access_level}
                                onChange={(e) => changeRole(u, e.target.value)}
                                disabled={u.id === me.id}
                                data-testid={`user-role-select-${u.id}`}
                            >
                                <option value="admin">Admin</option>
                                <option value="adviser">Adviser</option>
                            </select>
                            {removing === u.id ? (
                                <div className="flex items-center gap-1">
                                    <span style={{ fontSize: 11, color: "var(--tms-danger)", fontWeight: 500 }}>Remove?</span>
                                    <button className="btn-outline" onClick={() => setRemoving(null)}>No</button>
                                    <button className="btn-danger" onClick={() => remove(u)} data-testid={`user-remove-confirm-${u.id}`}>Yes</button>
                                </div>
                            ) : (
                                <button
                                    className="btn-ghost"
                                    onClick={() => setRemoving(u.id)}
                                    disabled={u.id === me.id}
                                    style={{ opacity: u.id === me.id ? 0.35 : 1 }}
                                    data-testid={`user-remove-${u.id}`}
                                >
                                    <Trash2 size={13} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--tms-border)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 8 }}>Invite a new user</div>
                <div className="grid gap-2 md:grid-cols-4">
                    <input placeholder="Name" className="tms-input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name-input" />
                    <input placeholder="Email" className="tms-input" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email-input" />
                    <input placeholder="Role title" className="tms-input" value={invite.role_title} onChange={(e) => setInvite({ ...invite, role_title: e.target.value })} data-testid="invite-role-title-input" />
                    <select className="tms-select" value={invite.access_level} onChange={(e) => setInvite({ ...invite, access_level: e.target.value })} data-testid="invite-access-level-select">
                        <option value="adviser">Adviser</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <div className="mt-2">
                    <input
                        type="text"
                        placeholder="Temporary password (min 10 chars — user is forced to change on first sign-in)"
                        className="tms-input"
                        value={invite.temporary_password}
                        onChange={(e) => setInvite({ ...invite, temporary_password: e.target.value })}
                        data-testid="invite-temporary-password-input"
                        autoComplete="off"
                    />
                </div>
                {inviteError && (
                    <div
                        data-testid="invite-error"
                        style={{ background: "#fdeceb", color: "var(--tms-danger)", padding: "6px 12px", borderRadius: 8, fontSize: 12, marginTop: 8 }}
                    >
                        {inviteError}
                    </div>
                )}
                <div className="mt-3">
                    <button className="btn-primary-green" onClick={sendInvite} disabled={busy} data-testid="invite-submit-btn">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Send invite
                    </button>
                </div>
            </div>

            {resetTarget && (
                <div
                    onClick={closeReset}
                    style={{ position: "fixed", inset: 0, background: "rgba(15, 25, 45, 0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                    data-testid="reset-password-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="tms-card"
                        style={{ padding: 20, width: "100%", maxWidth: 440 }}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <KeyRound size={16} style={{ color: "var(--tms-navy)" }} />
                            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--tms-navy)" }}>
                                Reset password for {resetTarget.name}
                            </div>
                        </div>
                        <p style={{ fontSize: 13, color: "var(--tms-muted)", marginBottom: 12, lineHeight: 1.5 }}>
                            Set a temporary password. {resetTarget.name.split(" ")[0]} will be signed out of every device and forced to choose their own password on next sign-in. Share the temporary password with them privately (chat, phone or in person) — the app does not send email.
                        </p>
                        <input
                            type="text"
                            className="tms-input"
                            placeholder="Temporary password (min 10 characters)"
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            data-testid="reset-password-input"
                            autoComplete="off"
                            autoFocus
                        />
                        {resetError && (
                            <div
                                data-testid="reset-password-error"
                                style={{ background: "#fdeceb", color: "var(--tms-danger)", padding: "6px 12px", borderRadius: 8, fontSize: 12, marginTop: 8 }}
                            >
                                {resetError}
                            </div>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <button className="btn-outline" onClick={closeReset} disabled={busy} data-testid="reset-password-cancel">
                                Cancel
                            </button>
                            <button
                                className="btn-primary-green"
                                onClick={submitReset}
                                disabled={busy || resetPassword.length < 10}
                                data-testid="reset-password-submit"
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />} Reset password
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}




/* -------------- Email styles -------------- */

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const SAMPLE_BODY_HTML = (
    '<p>Hi Alex,</p>' +
    '<p>Great news — your mortgage offer with <strong>Nationwide</strong> is ready.</p>' +
    '<table role="presentation" cellspacing="0" cellpadding="0" border="0" ' +
    'style="width:100%;border-collapse:separate;border-left:4px solid #8EC740;' +
    'background:#F3F5F7;margin:14px 0;">' +
    '<tr><td style="padding:12px 14px;font-family:Poppins,Arial,sans-serif;color:#1A1A1A;">' +
    '<div style="font-weight:600;color:#1A3E6D;margin-bottom:4px;font-size:13px;">Your offer</div>' +
    '<div style="font-size:13px;line-height:1.55;">Loan amount: <strong>£250,000</strong></div>' +
    '</td></tr></table>' +
    '<p>Kind regards,<br/><strong style="color:#1A3E6D;">Rohit</strong></p>'
);

function StylePreview({ style }) {
    const styles = previewLayoutStyles(style);
    return (
        <div style={{ ...styles.outer, borderRadius: 0 }} data-testid="style-mini-preview">
            <div
                style={{ ...styles.inner, borderRadius: 0, fontSize: 13, lineHeight: 1.55 }}
                dangerouslySetInnerHTML={{ __html: SAMPLE_BODY_HTML }}
            />
        </div>
    );
}

function AlignmentControl({ value, onChange }) {
    const opts = [
        { key: "left", label: "Left" },
        { key: "center", label: "Centre" },
        { key: "right", label: "Right" },
    ];
    return (
        <div className="flex items-center gap-2" data-testid="alignment-control">
            <label style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500, minWidth: 140 }}>Alignment</label>
            <div
                className="inline-flex"
                style={{ border: "1px solid var(--tms-border)", borderRadius: 8, overflow: "hidden", height: 30 }}
            >
                {opts.map((o) => (
                    <button
                        key={o.key}
                        type="button"
                        onClick={() => onChange(o.key)}
                        data-testid={`alignment-${o.key}`}
                        style={{
                            background: value === o.key ? "var(--tms-navy)" : "#fff",
                            color: value === o.key ? "#fff" : "var(--tms-navy)",
                            border: "none",
                            padding: "0 14px",
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: "pointer",
                        }}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ColorField({ label, value, onChange, testId }) {
    const valid = HEX_RE.test(value || "");
    return (
        <div className="flex items-center gap-2">
            <label style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500, minWidth: 140 }}>{label}</label>
            <input
                type="color"
                value={valid ? value : "#000000"}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                style={{ width: 34, height: 30, padding: 0, border: "1px solid var(--tms-border)", borderRadius: 6, cursor: "pointer" }}
                data-testid={`${testId}-picker`}
            />
            <input
                type="text"
                className="tms-input"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: 110, fontSize: 12, height: 30, padding: "0 10px" }}
                maxLength={7}
                data-testid={`${testId}-hex`}
            />
        </div>
    );
}

function NumberField({ label, value, onChange, min, max, testId, suffix = "" }) {
    return (
        <div className="flex items-center gap-2">
            <label style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500, minWidth: 140 }}>{label}</label>
            <input
                type="number"
                className="tms-input"
                value={value}
                min={min}
                max={max}
                onChange={(e) => onChange(parseInt(e.target.value || min, 10))}
                style={{ width: 90, fontSize: 12, height: 30, padding: "0 10px" }}
                data-testid={testId}
            />
            {suffix && <span style={{ fontSize: 12, color: "var(--tms-muted)" }}>{suffix}</span>}
        </div>
    );
}

function ToggleField({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testId}
            />
            <span style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500 }}>{label}</span>
        </label>
    );
}

const BLANK_STYLE = {
    name: "",
    outer_bg: "#FFFFFF",
    inner_bg: "#FFFFFF",
    outer_border_enabled: false,
    outer_border_color: "#E2E6EA",
    outer_border_width: 1,
    inner_border_enabled: false,
    inner_border_color: "#E2E6EA",
    inner_border_width: 1,
    outer_padding: 0,
    inner_padding: 0,
    content_width: 624,
    content_align: "left",
};

function StyleForm({ initial, existingNames, onCancel, onSave, busy }) {
    const [draft, setDraft] = useState(initial);
    const [err, setErr] = useState("");
    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const submit = async () => {
        setErr("");
        const trimmed = (draft.name || "").trim();
        if (!trimmed) { setErr("Name is required"); return; }
        if (trimmed.length > 40) { setErr("Name must be at most 40 characters"); return; }
        if (existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
            setErr("A style with this name already exists");
            return;
        }
        for (const k of ["outer_bg", "inner_bg", "outer_border_color", "inner_border_color"]) {
            if (!HEX_RE.test(draft[k])) { setErr(`${k} must be #RRGGBB hex`); return; }
        }
        await onSave({ ...draft, name: trimmed });
    };

    return (
        <div
            className="tms-card"
            style={{ padding: 20, marginTop: 12, border: "1px dashed var(--tms-navy)" }}
            data-testid="style-form"
        >
            <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
                <div className="space-y-4">
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 500, color: "var(--tms-muted)", display: "block", marginBottom: 6 }}>
                            Style name
                        </label>
                        <input
                            className="tms-input"
                            value={draft.name}
                            onChange={(e) => setField("name", e.target.value)}
                            maxLength={40}
                            data-testid="style-name-input"
                            placeholder="e.g. Personal, Marketing, Legal notice"
                            style={{ maxWidth: 360 }}
                        />
                    </div>

                    <AlignmentControl value={draft.content_align} onChange={(v) => setField("content_align", v)} />

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 8, letterSpacing: "0.06em", textTransform: "uppercase" }}>Outer canvas</div>
                        <div className="space-y-2">
                            <ColorField label="Background" value={draft.outer_bg} onChange={(v) => setField("outer_bg", v)} testId="outer-bg" />
                            <NumberField label="Padding" value={draft.outer_padding} onChange={(v) => setField("outer_padding", v)} min={0} max={60} testId="outer-padding-input" suffix="px" />
                            <ToggleField label="Show outer border" checked={draft.outer_border_enabled} onChange={(v) => setField("outer_border_enabled", v)} testId="outer-border-toggle" />
                            {draft.outer_border_enabled && (
                                <>
                                    <ColorField label="Border color" value={draft.outer_border_color} onChange={(v) => setField("outer_border_color", v)} testId="outer-border-color" />
                                    <div className="flex items-center gap-2">
                                        <label style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500, minWidth: 140 }}>Border width</label>
                                        <select
                                            className="tms-select"
                                            value={draft.outer_border_width}
                                            onChange={(e) => setField("outer_border_width", parseInt(e.target.value, 10))}
                                            style={{ width: 90, height: 30, fontSize: 12, padding: "0 24px 0 10px" }}
                                            data-testid="outer-border-width-select"
                                        >
                                            {[1, 2, 3].map((n) => <option key={n} value={n}>{n}px</option>)}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 8, letterSpacing: "0.06em", textTransform: "uppercase" }}>Inner panel</div>
                        <div className="space-y-2">
                            <ColorField label="Background" value={draft.inner_bg} onChange={(v) => setField("inner_bg", v)} testId="inner-bg" />
                            <NumberField label="Padding" value={draft.inner_padding} onChange={(v) => setField("inner_padding", v)} min={0} max={60} testId="inner-padding-input" suffix="px" />
                            <NumberField label="Content width" value={draft.content_width} onChange={(v) => setField("content_width", v)} min={480} max={700} testId="content-width-input" suffix="px" />
                            <ToggleField label="Show inner border" checked={draft.inner_border_enabled} onChange={(v) => setField("inner_border_enabled", v)} testId="inner-border-toggle" />
                            {draft.inner_border_enabled && (
                                <>
                                    <ColorField label="Border color" value={draft.inner_border_color} onChange={(v) => setField("inner_border_color", v)} testId="inner-border-color" />
                                    <div className="flex items-center gap-2">
                                        <label style={{ fontSize: 12, color: "var(--tms-navy)", fontWeight: 500, minWidth: 140 }}>Border width</label>
                                        <select
                                            className="tms-select"
                                            value={draft.inner_border_width}
                                            onChange={(e) => setField("inner_border_width", parseInt(e.target.value, 10))}
                                            style={{ width: 90, height: 30, fontSize: 12, padding: "0 24px 0 10px" }}
                                            data-testid="inner-border-width-select"
                                        >
                                            {[1, 2, 3].map((n) => <option key={n} value={n}>{n}px</option>)}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {err && (
                        <div style={{ background: "#fdeceb", color: "var(--tms-danger)", padding: "6px 12px", borderRadius: 8, fontSize: 12 }} data-testid="style-form-error">
                            {err}
                        </div>
                    )}

                    <div className="flex gap-2 pt-2 border-t" style={{ borderColor: "var(--tms-border)" }}>
                        <button className="btn-primary-green" onClick={submit} disabled={busy} data-testid="style-form-save">
                            {busy && <Loader2 size={14} className="animate-spin" />} Save style
                        </button>
                        <button className="btn-outline" onClick={onCancel} disabled={busy} data-testid="style-form-cancel">Cancel</button>
                    </div>
                </div>

                <div>
                    <div style={{ fontSize: 11, color: "var(--tms-muted)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>
                        Live preview
                    </div>
                    <StylePreview style={draft} />
                </div>
            </div>
        </div>
    );
}

/* -------------- Import templates -------------- */
function ImportTemplatesPanel() {
    const { reloadTemplates, reloadCategories } = useData();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null); // { plan, categories_to_create, summary }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [imported, setImported] = useState(null); // summary of last committed import

    const onPick = (e) => {
        setError("");
        setPreview(null);
        setImported(null);
        const f = e.target.files?.[0] || null;
        setFile(f);
    };

    const runPreview = async () => {
        if (!file) return;
        setBusy(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/templates/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the file");
        }
        setBusy(false);
    };

    const runImport = async () => {
        if (!file || !preview) return;
        setBusy(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/templates/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImported(r.data);
            toast.success(
                `Imported ${r.data.summary.create} template(s) as private drafts`,
                { position: "bottom-center", duration: 6000 },
            );
            await reloadTemplates();
            await reloadCategories();
            setPreview(null);
            setFile(null);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
        }
        setBusy(false);
    };

    const chipStyle = (status) => {
        const map = {
            create: { bg: "#DCF3D8", fg: "#1F5F1A" },
            skipped: { bg: "#FDF3D0", fg: "#7A6410" },
            error: { bg: "#FDECEB", fg: "#842029" },
        };
        const c = map[status] || map.error;
        return {
            display: "inline-block", padding: "2px 8px", borderRadius: 10,
            fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg,
        };
    };

    return (
        <div className="tms-card p-6" data-testid="settings-import-templates">
            <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet size={16} style={{ color: "var(--tms-navy)" }} />
                <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }}>
                    Import templates
                </h2>
            </div>
            <p style={{ fontSize: 12, color: "var(--tms-muted)", marginBottom: 14, lineHeight: 1.55 }}>
                Upload an .xlsx or .csv (max 2 MB) with columns <strong>Name</strong>, <strong>Subject</strong>, <strong>Body</strong>, <strong>Category</strong>, and optional <strong>Brand</strong>, <strong>Description</strong>, <strong>When to use</strong>. Imported templates land as <strong>private drafts</strong> owned by you — nothing goes live until you review and share it.
            </p>

            <div className="flex items-center gap-2 flex-wrap">
                <label
                    className="btn-outline"
                    style={{ cursor: "pointer" }}
                    data-testid="import-file-label"
                >
                    <Upload size={13} /> Choose file
                    <input
                        type="file"
                        accept=".xlsx,.csv"
                        onChange={onPick}
                        style={{ display: "none" }}
                        data-testid="import-file-input"
                    />
                </label>
                {file && (
                    <span style={{ fontSize: 12, color: "var(--tms-navy)" }} data-testid="import-file-name">
                        {file.name} · {Math.round(file.size / 1024)} KB
                    </span>
                )}
                {file && !preview && (
                    <button
                        className="btn-primary-green"
                        onClick={runPreview}
                        disabled={busy}
                        data-testid="import-preview-btn"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />} Preview
                    </button>
                )}
            </div>

            {error && (
                <div
                    data-testid="import-error"
                    style={{ marginTop: 12, background: "#fdeceb", color: "var(--tms-danger)", padding: "8px 12px", borderRadius: 8, fontSize: 12 }}
                >
                    {error}
                </div>
            )}

            {preview && (
                <div className="mt-4" data-testid="import-preview">
                    <div style={{ fontSize: 13, color: "var(--tms-navy)", marginBottom: 10 }} data-testid="import-summary">
                        <strong>{preview.summary.create}</strong> will be created as private drafts,
                        <strong> {preview.summary.skipped}</strong> skipped,
                        <strong> {preview.summary.errors}</strong> errors.
                    </div>
                    {preview.categories_to_create.length > 0 && (
                        <div
                            data-testid="import-new-categories"
                            style={{ fontSize: 12, color: "var(--tms-muted)", marginBottom: 10 }}
                        >
                            New categories that will be auto-created:{" "}
                            <strong style={{ color: "var(--tms-navy)" }}>
                                {preview.categories_to_create.join(", ")}
                            </strong>
                        </div>
                    )}
                    <div
                        style={{ maxHeight: 320, overflow: "auto", border: "1px solid var(--tms-border)", borderRadius: 8 }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: "#F3F5F7", position: "sticky", top: 0 }}>
                                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--tms-muted)", fontWeight: 600 }}>Row</th>
                                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--tms-muted)", fontWeight: 600 }}>Name</th>
                                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--tms-muted)", fontWeight: 600 }}>Category</th>
                                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--tms-muted)", fontWeight: 600 }}>Brand</th>
                                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--tms-muted)", fontWeight: 600 }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.plan.map((p, i) => (
                                    <tr
                                        key={i}
                                        style={{ borderTop: "1px solid var(--tms-border)" }}
                                        data-testid={`import-plan-row-${p.row}`}
                                    >
                                        <td style={{ padding: "6px 10px", color: "var(--tms-muted)" }}>{p.row}</td>
                                        <td style={{ padding: "6px 10px", color: "var(--tms-navy)", fontWeight: 500 }}>{p.name}</td>
                                        <td style={{ padding: "6px 10px" }}>{p.category || "—"}</td>
                                        <td style={{ padding: "6px 10px" }}>{p.brand_key || "—"}</td>
                                        <td style={{ padding: "6px 10px" }}>
                                            <span style={chipStyle(p.status)}>
                                                {p.status === "create" && "Will create"}
                                                {p.status === "skipped" && `Skipped — ${p.reason || ""}`}
                                                {p.status === "error" && `Error — ${p.reason || ""}`}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3 flex gap-2">
                        <button
                            className="btn-primary-green"
                            onClick={runImport}
                            disabled={busy || preview.summary.create === 0}
                            data-testid="import-commit-btn"
                        >
                            {busy && <Loader2 size={14} className="animate-spin" />}
                            Import {preview.summary.create} template{preview.summary.create === 1 ? "" : "s"}
                        </button>
                        <button
                            className="btn-outline"
                            onClick={() => { setPreview(null); setFile(null); }}
                            disabled={busy}
                            data-testid="import-cancel-btn"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {imported && (
                <div
                    className="mt-4"
                    data-testid="import-result"
                    style={{ background: "#F0FBF3", border: "1px solid #C6E7CB", padding: "10px 14px", borderRadius: 10, fontSize: 13, color: "#1F5F1A" }}
                >
                    Imported <strong>{imported.summary.create}</strong> template(s) from{" "}
                    <em>{imported.filename}</em>. {imported.summary.skipped} skipped,{" "}
                    {imported.summary.errors} errors. They&apos;re in your library as private drafts.
                </div>
            )}
        </div>
    );
}


function EmailStylesPanel() {
    const { brands, emailStyles, reloadEmailStyles, reloadBrands } = useData();
    const [editingId, setEditingId] = useState(null); // null = none, "new" = create form, otherwise style_id
    const [busy, setBusy] = useState(false);

    const editingStyle = editingId && editingId !== "new" ? emailStyles.find((s) => s.id === editingId) : null;

    const existingNames = emailStyles.map((s) => s.name);
    const namesExcludingEditing = editingStyle
        ? existingNames.filter((n) => n.toLowerCase() !== editingStyle.name.toLowerCase())
        : existingNames;

    const saveStyle = async (payload) => {
        setBusy(true);
        try {
            if (editingId === "new") {
                await api.post("/email-styles", payload);
                toast.success("Style created", { position: "bottom-center" });
            } else {
                await api.put(`/email-styles/${editingId}`, payload);
                toast.success("Style saved", { position: "bottom-center" });
            }
            await reloadEmailStyles();
            setEditingId(null);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const deleteStyle = async (s) => {
        if (!window.confirm(`Delete style "${s.name}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/email-styles/${s.id}`);
            toast.success("Style deleted", { position: "bottom-center" });
            await reloadEmailStyles();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    const setBrandDefault = async (brand, styleId) => {
        setBusy(true);
        try {
            await api.put(`/brands/${brand.key}/default-style`, { style_id: styleId });
            toast.success(`${brand.label}'s default style updated`, { position: "bottom-center" });
            await reloadBrands();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed", { position: "bottom-center" });
        }
        setBusy(false);
    };

    return (
        <div className="tms-card p-6" data-testid="settings-email-styles">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Layers size={16} style={{ color: "var(--tms-navy)" }} />
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }}>Email styles</h2>
                </div>
                <button
                    className="btn-primary-green"
                    onClick={() => setEditingId("new")}
                    disabled={!!editingId}
                    data-testid="style-new-btn"
                >
                    <Plus size={14} /> New style
                </button>
            </div>
            <p style={{ fontSize: 12, color: "var(--tms-muted)", marginBottom: 14, lineHeight: 1.55 }}>
                Named formatting presets shared across brands. Solid borders only (Outlook doesn&apos;t render rounded corners reliably). Styles never carry content — only the wrapper look.
            </p>

            {/* Per-brand default pickers */}
            <div
                className="grid gap-3 mb-5 p-4"
                style={{ background: "#F3F5F7", borderRadius: 10 }}
                data-testid="brand-default-styles"
            >
                <div style={{ fontSize: 11, color: "var(--tms-muted)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    Default style per brand
                </div>
                {brands.map((b) => (
                    <div key={b.key} className="flex items-center gap-3">
                        <span style={{ fontSize: 13, color: "var(--tms-navy)", minWidth: 220, fontWeight: 500 }}>{b.label}</span>
                        <select
                            className="tms-select"
                            value={b.default_style_id || ""}
                            onChange={(e) => setBrandDefault(b, e.target.value)}
                            disabled={busy}
                            style={{ maxWidth: 260 }}
                            data-testid={`brand-default-select-${b.key}`}
                        >
                            {emailStyles.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>

            {/* Style list */}
            <div className="space-y-2">
                {emailStyles.map((s) => {
                    const templateCount = s.usage?.template_count || 0;
                    const brandKeys = s.usage?.brand_default_keys || [];
                    const brandLabels = brandKeys
                        .map((k) => brands.find((b) => b.key === k)?.label || k)
                        .join(", ");
                    const usageBits = [];
                    if (templateCount) usageBits.push(`${templateCount} template${templateCount === 1 ? "" : "s"}`);
                    if (brandLabels) usageBits.push(`default for ${brandLabels}`);
                    const usageStr = usageBits.length ? usageBits.join(" • ") : "not in use";
                    const canDelete = emailStyles.length > 1 && templateCount === 0 && brandKeys.length === 0;
                    return (
                        <div
                            key={s.id}
                            className="flex items-center justify-between gap-3 p-3"
                            style={{ border: "1px solid var(--tms-border)", borderRadius: 8 }}
                            data-testid={`style-row-${s.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div style={{ fontSize: 14, color: "var(--tms-navy)", fontWeight: 500 }}>{s.name}</div>
                                <div style={{ fontSize: 11, color: "var(--tms-muted)", marginTop: 2 }} data-testid={`style-usage-${s.id}`}>
                                    {s.content_align} · {usageStr}
                                </div>
                            </div>
                            <button
                                className="btn-ghost"
                                onClick={() => setEditingId(s.id)}
                                disabled={busy || !!editingId}
                                data-testid={`style-edit-${s.id}`}
                            >
                                <Pencil size={12} /> Edit
                            </button>
                            <button
                                className="btn-ghost"
                                onClick={() => deleteStyle(s)}
                                disabled={busy || !!editingId || !canDelete}
                                title={!canDelete ? "In use — reassign templates or change brand defaults first" : "Delete"}
                                data-testid={`style-delete-${s.id}`}
                                style={{ color: canDelete ? "var(--tms-danger)" : "#c0c6cc" }}
                            >
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    );
                })}
            </div>

            {editingId === "new" && (
                <StyleForm
                    initial={BLANK_STYLE}
                    existingNames={existingNames}
                    busy={busy}
                    onCancel={() => setEditingId(null)}
                    onSave={saveStyle}
                />
            )}
            {editingStyle && (
                <StyleForm
                    initial={editingStyle}
                    existingNames={namesExcludingEditing}
                    busy={busy}
                    onCancel={() => setEditingId(null)}
                    onSave={saveStyle}
                />
            )}
        </div>
    );
}


/* -------------- Page -------------- */
export default function SettingsPage() {
    return (
        <div className="max-w-[1080px] mx-auto px-6 py-6 space-y-6" data-testid="settings-page">
            <h1 style={{ fontSize: 26, fontWeight: 600, color: "var(--tms-navy)" }}>Settings</h1>
            <ErrorBoundary label="categories"><CategoriesPanel /></ErrorBoundary>
            <ErrorBoundary label="brands"><BrandsPanel /></ErrorBoundary>
            <ErrorBoundary label="email-styles"><EmailStylesPanel /></ErrorBoundary>
            <ErrorBoundary label="import-templates"><ImportTemplatesPanel /></ErrorBoundary>
            <ErrorBoundary label="users"><UsersPanel /></ErrorBoundary>
        </div>
    );
}
